/**
 * 形状工具落盘（补齐 registry.ts 早先登记的占位工具，复用钢笔工具已经建好的
 * 形状图层管线：engine/path/anchor.ts 的几何 + app/commands/shape-commands.ts
 * 的图层命令）。矩形/椭圆/直线都只是"用哪组锚点"的区别，落盘方式跟钢笔
 * 收笔时一模一样——一次调用 = 新增一个形状图层 = 一条历史。
 */
import {
  ellipseAnchors,
  lineAnchors,
  pathToCommands,
  roundedRectangleAnchors,
  type Point,
} from '../engine/path/anchor';
import { createAddLayerCommand } from './commands/layer-commands';
import { createShapeLayer } from './commands/shape-commands';
import { layerId } from '../engine/tile/key';
import type { AppStore } from './store';
import type { VectorFill, VectorStroke } from '../document/types';

export type ShapeKind = 'rectangle' | 'ellipse' | 'line';

let layerSeq = 0;

/**
 * 用拖拽的起止点建一个形状图层。`lineWeight` 只有 kind==='line' 时用得上。
 * 起止点重合（拖拽距离为零）时无操作——没有意义的零尺寸形状不建历史。
 * 返回是否创建成功。
 */
export function createShapeFromDrag(
  store: AppStore,
  kind: ShapeKind,
  from: Point,
  to: Point,
  lineWeight: number,
  fill: VectorFill = { type: 'solid', color: '#808080' },
  stroke?: VectorStroke,
  /** 矩形的圆角半径（文档像素）；其它 kind 忽略。缺省 0 = 直角。 */
  radius = 0,
): boolean {
  const document = store.document;
  if (document === null) return false;
  if (kind !== 'line' && from.x === to.x && from.y === to.y) return false;

  const anchors =
    kind === 'rectangle'
      ? roundedRectangleAnchors(
          Math.min(from.x, to.x),
          Math.min(from.y, to.y),
          Math.max(from.x, to.x),
          Math.max(from.y, to.y),
          radius,
        )
      : kind === 'ellipse'
        ? ellipseAnchors((from.x + to.x) / 2, (from.y + to.y) / 2, Math.abs(to.x - from.x) / 2, Math.abs(to.y - from.y) / 2)
        : lineAnchors(from, to, lineWeight);

  const commands = pathToCommands({ anchors, closed: true, fillRule: 'nonzero' });
  const id = layerId(`shape-${++layerSeq}-${document.root.children.length}`);
  const layer = createShapeLayer(
    id,
    `形状 ${document.root.children.length + 1}`,
    { commands, fillRule: 'nonzero' },
    fill,
    stroke,
  );
  store.dispatch(createAddLayerCommand({ layer, parentId: null, index: document.root.children.length }));
  store.setActiveLayer(id);
  return true;
}
